/**
 * HeadlineForm
 *
 * Reusable form for creating or editing a headline.
 * Rendered inside the right-side slide panel for both create and edit modes.
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { AttachmentsList } from '@/features/attachments';
import { LinkedTodoCreator } from '@/features/os-todos';
import { useSession } from '@/hooks/useSession';
import { useCreateHeadline, useUpdateHeadline } from '../hooks/useHeadlines';
import { useTeams } from '@/features/rocks/hooks/useTeams';
import { useOwnerOptions } from '@/features/rocks/hooks/useRocks';
import type { Headline, UpdateHeadlineInput } from '../types';
import type { Team } from '@/features/rocks/types';

interface HeadlineFormProps {
  open: boolean;
  onClose: () => void;
  /** Pass a headline to edit, or null/undefined to create */
  headline?: Headline | null;
  /** Optional teams list (if not provided, will be loaded) */
  teams?: Team[];
}

/** Convert an ISO timestamp to the `<input type="date">` value (yyyy-mm-dd, local tz). */
function toDateLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Today in yyyy-mm-dd format (local tz). */
function todayDateLocal(): string {
  return toDateLocal(new Date().toISOString());
}

/**
 * Convert a `<input type="date">` yyyy-mm-dd value to an ISO timestamp (local
 * start-of-day). Used for the scheduled_at field so the server stores a
 * concrete moment while the UI stays date-only.
 */
function fromDateLocal(value: string): string | null {
  if (!value) return null;
  // Parse as local time at midnight so "today" publishes immediately.
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Cheap set-equality check for team id arrays. */
function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((id) => s.has(id));
}

export function HeadlineForm({ open, onClose, headline, teams: providedTeams }: HeadlineFormProps) {
  const isEditing = !!headline?.id;

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [ownerId, setOwnerId] = useState('');
  /** Empty = publish now. Otherwise a <input type="datetime-local"> value. */
  const [scheduledAt, setScheduledAt] = useState('');

  const createMutation = useCreateHeadline();
  const updateMutation = useUpdateHeadline();
  const { data: loadedTeams = [] } = useTeams();
  const { data: owners = [] } = useOwnerOptions();
  const { user } = useSession();
  const teams = providedTeams || loadedTeams;
  const isPending = createMutation.isPending || updateMutation.isPending;

  const initialized = useRef(false);
  // Snapshot of the original headline values when the form first loads. Used
  // on submit so we only send fields the user actually changed — prevents a
  // stale or still-loading state from clobbering existing DB values.
  const initialSnapshot = useRef<{
    title: string;
    body: string | null;
    teamIds: string[];
    ownerId: string;
    ownerName: string | null;
    scheduledAtLocal: string;
  } | null>(null);

  useEffect(() => {
    if (!open) {
      initialized.current = false;
      return;
    }
    if (initialized.current) return;

    if (headline?.id) {
      const initTitle = headline.title;
      const initBody = headline.body || '';
      const initTeamIds = headline.team_ids ?? [];
      const initOwnerId = headline.owner_id ?? '';
      // Display the publish date in the form. If scheduled_at is set, use it.
      // Otherwise fall back to created_at so legacy headlines (with null
      // scheduled_at) still show when they went out — matches the table column.
      // The submit comparison uses the same initial value, so we don't
      // accidentally persist created_at as scheduled_at if the user doesn't
      // touch the field.
      const initScheduledAt = headline.scheduled_at
        ? toDateLocal(headline.scheduled_at)
        : headline.created_at
          ? toDateLocal(headline.created_at)
          : '';
      setTitle(initTitle);
      setBody(initBody);
      setTeamIds(initTeamIds);
      setOwnerId(initOwnerId);
      setScheduledAt(initScheduledAt);
      initialSnapshot.current = {
        title: initTitle,
        body: headline.body ?? null,
        teamIds: initTeamIds,
        ownerId: initOwnerId,
        ownerName: headline.owner_name ?? null,
        scheduledAtLocal: initScheduledAt,
      };
      initialized.current = true;
    } else if (owners.length > 0) {
      // Create mode: wait for the owners list before initialising so we can
      // look up the creator by email and default them as the owner.
      setTitle('');
      setBody('');
      setTeamIds([]);
      const defaultOwner = user?.email
        ? owners.find((o) => o.email?.toLowerCase() === user.email.toLowerCase())
        : undefined;
      setOwnerId(defaultOwner?.id ?? '');
      // Default publish date to today — submits as an immediate publish.
      // Users can push it to a future date to make it an upcoming headline.
      setScheduledAt(todayDateLocal());
      initialSnapshot.current = null;
      initialized.current = true;
    }
  }, [open, headline, owners, user?.email]);

  function toggleTeam(teamId: string, checked: boolean) {
    setTeamIds((prev) =>
      checked ? Array.from(new Set([...prev, teamId])) : prev.filter((id) => id !== teamId)
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;

    const resolvedOwnerId = ownerId || null;
    const resolvedOwnerName = resolvedOwnerId
      ? owners.find((o) => o.id === resolvedOwnerId)?.name
          ?? (headline?.owner_id === resolvedOwnerId ? headline?.owner_name ?? null : null)
      : null;
    const resolvedBody = body.trim() || null;

    if (isEditing && headline) {
      // Send only the fields that actually changed vs. the initial snapshot.
      // Prevents a stale / still-loading form state from clobbering existing
      // DB values (owner, publish date, body) on save.
      const snap = initialSnapshot.current;
      const data: UpdateHeadlineInput = {};
      if (!snap) {
        // Snapshot missing — be safe and send the full payload.
        data.title = title.trim();
        data.body = resolvedBody;
        data.team_ids = teamIds;
        data.owner_id = resolvedOwnerId;
        data.owner_name = resolvedOwnerName;
        data.scheduled_at = fromDateLocal(scheduledAt);
      } else {
        if (title.trim() !== snap.title) data.title = title.trim();
        if (resolvedBody !== snap.body) data.body = resolvedBody;
        if (!sameIdSet(teamIds, snap.teamIds)) data.team_ids = teamIds;
        if (resolvedOwnerId !== (snap.ownerId || null)) {
          data.owner_id = resolvedOwnerId;
          data.owner_name = resolvedOwnerName;
        }
        if (scheduledAt !== snap.scheduledAtLocal) {
          data.scheduled_at = fromDateLocal(scheduledAt);
        }
      }

      if (Object.keys(data).length === 0) {
        // Nothing changed — just close.
        onClose();
        return;
      }

      updateMutation.mutate(
        { id: headline.id, data },
        { onSuccess: () => onClose() }
      );
    } else {
      createMutation.mutate(
        {
          title: title.trim(),
          body: body.trim() || undefined,
          type: 'headline',
          team_ids: teamIds,
          owner_id: resolvedOwnerId,
          owner_name: resolvedOwnerName,
          scheduled_at: fromDateLocal(scheduledAt),
        },
        { onSuccess: () => onClose() }
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      <div className="grid gap-2">
        <Label htmlFor="headline-title">Title *</Label>
        <Input
          id="headline-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter a headline..."
          maxLength={500}
          autoFocus
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="headline-body">Details</Label>
        <Textarea
          id="headline-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Optional details or context..."
          className="text-sm min-h-[200px]"
          rows={8}
        />
      </div>

      <div className="grid gap-2">
        <Label>
          Teams *
          {teamIds.length > 0 && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              ({teamIds.length} selected)
            </span>
          )}
        </Label>
        <div className="space-y-2 max-h-40 overflow-y-auto rounded-md border p-3">
          {teams.length === 0 ? (
            <p className="text-sm text-muted-foreground">No teams available.</p>
          ) : (
            teams.map((team) => (
              <div key={team.id} className="flex items-center gap-2">
                <Checkbox
                  id={`headline-team-${team.id}`}
                  checked={teamIds.includes(team.id)}
                  onCheckedChange={(checked) => toggleTeam(team.id, checked === true)}
                />
                <Label htmlFor={`headline-team-${team.id}`} className="font-normal text-sm">
                  {team.name}
                </Label>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="headline-owner">Owner</Label>
        <Select
          value={ownerId || '_unassigned'}
          onValueChange={(v) => {
            // Radix Select fires onValueChange('') when the controlled value
            // briefly doesn't match a rendered SelectItem (e.g. owners list
            // still loading). Ignoring it prevents the owner from being
            // silently wiped to Unassigned on mount.
            if (v === '') return;
            setOwnerId(v === '_unassigned' ? '' : v);
          }}
        >
          <SelectTrigger id="headline-owner">
            <SelectValue placeholder="Select owner..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_unassigned">Unassigned</SelectItem>
            {/* Fallback: ensure the current owner always has a matching SelectItem,
                even if they're not in the active employees list (disabled, loading, etc.).
                Without this, Radix Select silently drops the value. */}
            {ownerId && !owners.some((o) => o.id === ownerId) && (
              <SelectItem value={ownerId}>
                {headline?.owner_name ?? 'Current owner'}
              </SelectItem>
            )}
            {owners.map((owner) => (
              <SelectItem key={owner.id} value={owner.id}>
                {owner.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="headline-scheduled-at">
          Publish date
          {(() => {
            if (!scheduledAt) return null;
            // Compare by date (local midnight) — future date = upcoming.
            const picked = new Date(`${scheduledAt}T00:00:00`).getTime();
            const todayMidnight = new Date(todayDateLocal() + 'T00:00:00').getTime();
            const isFuture = picked > todayMidnight;
            return (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {isFuture
                  ? '(upcoming — hidden from team until this date)'
                  : '(published)'}
              </span>
            );
          })()}
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id="headline-scheduled-at"
            type="date"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="text-sm"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setScheduledAt(todayDateLocal())}
          >
            Today
          </Button>
        </div>
      </div>

      {isEditing && headline?.id && (
        <div className="grid gap-2">
          <Label>Attachments</Label>
          <AttachmentsList entityType="headline" entityId={headline.id} />
        </div>
      )}

      {isEditing && headline?.id && (
        <div className="grid gap-2">
          <Label>Linked To-Do</Label>
          <LinkedTodoCreator
            sourceType="headline"
            sourceId={headline.id}
            teamId={headline.team_ids?.[0] ?? null}
          />
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!title.trim() || teamIds.length === 0 || isPending}
        >
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEditing ? 'Save' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
